import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import { formatEventStatusTabLabel } from "../../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import EvaluationApplicantCard from "./EvaluationApplicantCard";
import RatingForm from "./RatingForm";

// Company portal wrapper: breadcrumbs + heading + applicant card around the shared
// RatingForm, configured with the performance-indicator criteria coming from the
// evaluation form's questions (dynamic — question text is the API `body`).
const EvaluateApplicantForm = ({
  applicant,
  round,
  event,
  questions,
  eventsListPath,
  onSubmit,
  onCancel,
  onNavigateToEvent,
  onNavigateToEvaluations,
  onNavigateToRound,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents.evaluationsTab.applicantsView.evaluateForm;

  const breadcrumbList = [
    {
      label: events.listEvents.listEventsBreadcrumb,
      path: eventsListPath,
    },
    {
      label: formatEventStatusTabLabel(event?.status, events.listEvents.eventTabs),
      path: event?.status
        ? `${eventsListPath}?tab=${event.status}`
        : eventsListPath,
    },
    {
      label: event?.name || "",
      path: "",
      isClicked: true,
      onclick: onNavigateToEvent,
    },
    {
      label: events.viewEvents.evaluations,
      path: "",
      isClicked: true,
      onclick: onNavigateToEvaluations,
    },
    {
      label: round?.name ?? "",
      path: "",
      isClicked: true,
      onclick: onNavigateToRound,
    },
    {
      label: applicant?.applicantName ?? "",
    },
  ];

  const criteriaLabels = useMemo(
    () =>
      questions.reduce(
        (acc, q) => ({ ...acc, [String(q.id)]: q.body }),
        {},
      ),
    [questions],
  );

  const sections = [
    {
      key: "performance",
      title: t.performanceIndicators,
      criteriaKeys: questions.map((q) => String(q.id)),
      action: (
        <Typography className="eval-applicant-form__hint text-label-sm">
          {t.clickToRate}
        </Typography>
      ),
    },
  ];

  const handleSubmit = ({ ratings, feedback }) => {
    onSubmit({
      comment: feedback,
      evaluation_form_questions_attributes: questions.map((q) => ({
        id: q.id,
        rate: ratings[String(q.id)],
      })),
    });
  };

  return (
    <Box className="eval-applicant-form">
      <Breadcrumbs list={breadcrumbList} />

      <Box>
        <Typography className="eval-applicant-form__title text-heading-xl">
          {t.title}
        </Typography>
        <Typography className="eval-applicant-form__subtitle text-body-sm">
          {t.subtitle.replace("{roundName}", round?.name ?? "")}
        </Typography>
      </Box>

      {applicant && <EvaluationApplicantCard applicant={applicant} />}

      <RatingForm
        sections={sections}
        criteriaLabels={criteriaLabels}
        layout="grid"
        feedbackTitle={t.detailedFeedback}
        feedbackPlaceholder={t.feedbackPlaceholder}
        statusSectionLabel={t.evaluationStatus}
        statusLabel={t.statusInProgress}
        submitLabel={t.submit}
        cancelLabel={t.cancel}
        onSubmit={handleSubmit}
        onCancel={onCancel}
      />
    </Box>
  );
};

EvaluateApplicantForm.propTypes = {
  applicant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    applicantName: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    zoneName: PropTypes.string.isRequired,
  }),
  round: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      body: PropTypes.string,
      rate: PropTypes.number,
    }),
  ),
  eventsListPath: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onNavigateToEvent: PropTypes.func,
  onNavigateToEvaluations: PropTypes.func,
  onNavigateToRound: PropTypes.func,
};

EvaluateApplicantForm.defaultProps = {
  applicant: null,
  round: null,
  event: null,
  questions: [],
  eventsListPath: ROUTE_PATHS.companyEvents,
  onNavigateToEvent: undefined,
  onNavigateToEvaluations: undefined,
  onNavigateToRound: undefined,
};

export default EvaluateApplicantForm;
